import { Badge, MediumText, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import type { ComponentProps } from 'react'
import {
  BeakerIcon,
  SparklesIcon,
  DevicePhoneMobileIcon,
  CubeIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
} from '@heroicons/react/20/solid'
import { TIMELINE_EVENTS } from '../constants'
import { uiClasses } from '../../shared/uiClasses'

const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint
const BAR_TRACK = uiClasses.barTrack

type BadgeColor = ComponentProps<typeof Badge>['variant']
type TimelineTypeConfig = {
  badge: BadgeColor
  icon: typeof BeakerIcon
  label: string
}

const TYPE_CONFIG: Record<string, TimelineTypeConfig> = {
  lab: { icon: BeakerIcon, badge: 'sky', label: 'Lab' },
  ai: { icon: SparklesIcon, badge: 'amber', label: 'AI Report' },
  wearable: {
    icon: DevicePhoneMobileIcon,
    badge: 'violet',
    label: 'Wearable',
  },
  medication: {
    icon: CubeIcon,
    badge: 'emerald',
    label: 'Medication',
  },
  appointment: {
    icon: CalendarDaysIcon,
    badge: 'rose',
    label: 'Appointment',
  },
  record: {
    icon: DocumentTextIcon,
    badge: 'zinc',
    label: 'Record',
  },
}

export default function TimelineTab() {
  return (
    <div className="space-y-6">
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Health timeline</Subheading>
      </SurfaceCard>

      {/* Timeline feed */}
      <div className="relative space-y-0">
        {/* Vertical line */}
        <div className={`absolute left-5 top-0 bottom-0 w-px ${BAR_TRACK}`} />

        {TIMELINE_EVENTS.map((event, i) => {
          const config = TYPE_CONFIG[event.type] ?? TYPE_CONFIG.record
          const Icon = config.icon
          const showDateHeader =
            i === 0 || TIMELINE_EVENTS[i - 1].date !== event.date

          return (
            <div key={event.id}>
              {/* Date header */}
              {showDateHeader && (
                <div className="relative z-10 flex items-center gap-3 py-3 pl-12">
                  <span className={`text-xs/5 font-semibold uppercase tracking-wide ${TEXT_FAINT}`}>
                    {event.date}
                  </span>
                </div>
              )}

              {/* Event card */}
              <div className="relative flex gap-4 py-2 pl-0">
                {/* Icon dot */}
                <div
                  className={`relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full ${uiClasses.iconSurface}`}
                >
                  <Icon className={`size-4.5 ${TEXT_SECONDARY}`} />
                </div>

                {/* Content */}
                <SurfaceRow className="flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <MediumText tone="primary">{event.title}</MediumText>
                      <p className={`text-xs/5 ${TEXT_SUBTLE}`}>
                        {event.source} · {event.time}
                      </p>
                    </div>
                    <Badge variant={config.badge}>
                      {config.label}
                    </Badge>
                  </div>
                  <p className={`mt-2 text-sm/6 ${TEXT_SECONDARY}`}>{event.detail}</p>
                </SurfaceRow>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
